import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Autocomplete,
  TextField,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';

// ============================================================
// Types (inline, matching API responses)
// ============================================================

interface CustomerRecord {
  id: string;
  customerCode: string;
  name: string;
  nameKana: string | null;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  notes: string | null;
}

interface ContractDto {
  id: string;
  customerId: string;
  contractType: string | null;
  systemName: string;
  totalFee: number | null;
  monthlyFee: number | null;
  fiveYearFee: number | null;
  paymentMonth: string | null;
  startDate: string | null;
  renewalDate: string | null;
  personInCharge: string | null;
  customer: { id: string; name: string; customerCode: string };
}

interface AssetItem {
  id: string;
  assetNumber: string | null;
  assetName: string | null;
  category: string;
  status: string | null;
  maintenanceDeadline: string | null;
  customer: { id: string; name: string; customerCode: string } | null;
}

interface LicenseDto {
  id: string;
  customerId: string;
  licenseName: string;
  licenseCount: number | null;
  assignedCount: number | null;
  lastRenewalDate: string | null;
  customer: { id: string; name: string; customerCode: string } | null;
}

interface CustomerOption {
  id: string;
  customerCode: string;
  name: string;
}

// ============================================================
// Helpers
// ============================================================

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}/${m}/${day}`;
}

function getDeadlineColor(dateStr: string | null | undefined): string | undefined {
  if (!dateStr) return undefined;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return undefined;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const diffMs = d.getTime() - now.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  if (diffDays < 0) return '#d32f2f'; // expired
  if (diffDays <= 30) return '#ed6c02'; // near expiry
  return undefined;
}

const CATEGORY_LABELS: Record<string, string> = {
  pc: 'PC',
  server: 'サーバー',
  nas: 'NAS',
  network_device: 'ネットワーク機器',
  monitor: 'モニター',
  printer: 'プリンター',
  software_license: 'ソフトウェアライセンス',
  smartphone: 'スマートフォン',
  tablet: 'タブレット',
  other: 'その他備品',
};

const STATUS_LABELS: Record<string, string> = {
  in_use: '使用中',
  in_storage: '保管中',
  under_repair: '修理中',
  disposed: '廃棄済み',
  lost: '紛失',
};

// ============================================================
// Component
// ============================================================

const CustomerKartePage: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();



  // --- Data fetching ---

  // Customer list for autocomplete
  const { data: customersRaw } = useQuery<{ data: CustomerOption[] }>({
    queryKey: ['customers-list'],
    queryFn: async () => {
      const res = await api.get<{ data: CustomerOption[] }>('/customers');
      return res.data;
    },
  });
  const customerList = customersRaw?.data;

  // Customer detail
  const {
    data: customer,
    isLoading: customerLoading,
    isError: customerError,
  } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: async () => {
      const res = await api.get<CustomerRecord>(`/customers/${customerId}`);
      return res.data;
    },
    enabled: !!customerId,
  });

  // Contracts
  const { data: contracts, isLoading: contractsLoading } = useQuery({
    queryKey: ['contracts', customerId],
    queryFn: async () => {
      const res = await api.get<{ data: ContractDto[] }>('/contracts', {
        params: { customerId },
      });
      return res.data.data;
    },
    enabled: !!customerId,
  });

  // Assets
  const { data: assets, isLoading: assetsLoading } = useQuery({
    queryKey: ['assets', customerId],
    queryFn: async () => {
      const res = await api.get<{ data: AssetItem[]; pagination: unknown }>('/assets', {
        params: { customerId, limit: 10000 },
      });
      return res.data.data;
    },
    enabled: !!customerId,
  });

  // Licenses
  const { data: licenses, isLoading: licensesLoading } = useQuery({
    queryKey: ['licenses', customerId],
    queryFn: async () => {
      const res = await api.get<{ data: LicenseDto[] }>('/licenses', {
        params: { customerId },
      });
      return res.data.data;
    },
    enabled: !!customerId,
  });

  // --- Handlers ---

  const handleCustomerChange = (_: unknown, value: CustomerOption | null) => {
    if (value) {
      navigate(`/customers/${value.id}/karte`);
    }
  };



  const contractCount = contracts?.length ?? 0;
  const assetCount = assets?.length ?? 0;
  const licenseCount = licenses?.length ?? 0;

  const customerName = customer?.name || customer?.customerCode || '';

  // --- Render ---

  return (
    <Box sx={{ p: 3, overflow: 'auto', height: 'calc(100vh - 48px)' }}>
      {/* Customer selector */}
      <Box sx={{ mb: 3 }}>
        <Autocomplete
          options={customerList ?? []}
          getOptionLabel={(opt) => `${opt.customerCode} - ${opt.name}`}
          value={customerList?.find((c) => c.id === customerId) ?? null}
          onChange={handleCustomerChange}
          renderInput={(params) => (
            <TextField {...params} label="顧客を選択" size="small" />
          )}
          sx={{ maxWidth: 480 }}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
        />
      </Box>

      {/* Loading / Error for customer */}
      {customerLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {customerError && (
        <Alert severity="error">顧客が見つかりません</Alert>
      )}

      {customer && (
        <>
          {/* 1. Customer basic info */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h4" fontWeight="bold">
              {customer.name}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              顧客コード：{customer.customerCode}
            </Typography>
            <Grid container spacing={2}>
              {customer.contactName && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    担当者：{customer.contactName}
                  </Typography>
                </Grid>
              )}
              {customer.phone && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    電話：{customer.phone}
                  </Typography>
                </Grid>
              )}
              {customer.email && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    メール：{customer.email}
                  </Typography>
                </Grid>
              )}
              {customer.address && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    住所：{customer.address}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Contracts section */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">契約情報</Typography>
              <Button
                size="small"
                onClick={() =>
                  navigate(`/contracts?customerId=${customerId}&customerName=${encodeURIComponent(customerName)}`)
                }
              >
                すべて表示（{contractCount}件）
              </Button>
            </Box>
            {contractsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : contracts && contracts.length > 0 ? (
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>システム名</TableCell>
                      <TableCell>契約種別</TableCell>
                      <TableCell align="right">月額</TableCell>
                      <TableCell>開始日</TableCell>
                      <TableCell>更新日</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contracts.slice(0, 5).map((c) => (
                      <TableRow
                        key={c.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() =>
                          navigate(`/contracts?customerId=${customerId}&customerName=${encodeURIComponent(customerName)}`)
                        }
                      >
                        <TableCell>{c.systemName}</TableCell>
                        <TableCell>{c.contractType ?? '—'}</TableCell>
                        <TableCell align="right">
                          {c.monthlyFee != null ? c.monthlyFee.toLocaleString() : '—'}
                        </TableCell>
                        <TableCell>{formatDate(c.startDate)}</TableCell>
                        <TableCell>{formatDate(c.renewalDate)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                契約情報はありません
              </Typography>
            )}
          </Paper>

          {/* Assets section */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">資産情報</Typography>
              <Button
                size="small"
                onClick={() =>
                  navigate(`/assets?customerId=${customerId}&customerName=${encodeURIComponent(customerName)}`)
                }
              >
                すべて表示（{assetCount}件）
              </Button>
            </Box>
            {assetsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : assets && assets.length > 0 ? (
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>資産番号</TableCell>
                      <TableCell>資産名称</TableCell>
                      <TableCell>資産区分</TableCell>
                      <TableCell>状態</TableCell>
                      <TableCell>保守期限</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assets.slice(0, 15).map((a) => (
                      <TableRow
                        key={a.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/assets?customerId=${customerId}&customerName=${encodeURIComponent(customerName)}`)}
                      >
                        <TableCell>{a.assetNumber ?? '—'}</TableCell>
                        <TableCell>{a.assetName ?? '—'}</TableCell>
                        <TableCell>{CATEGORY_LABELS[a.category] ?? a.category}</TableCell>
                        <TableCell>{(a.status && STATUS_LABELS[a.status]) ?? a.status ?? '—'}</TableCell>
                        <TableCell
                          sx={{
                            color: getDeadlineColor(a.maintenanceDeadline),
                            fontWeight: getDeadlineColor(a.maintenanceDeadline) ? 'bold' : undefined,
                          }}
                        >
                          {formatDate(a.maintenanceDeadline)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                資産情報はありません
              </Typography>
            )}
          </Paper>

          {/* Licenses section */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">ライセンス情報</Typography>
              <Button
                size="small"
                onClick={() =>
                  navigate(`/licenses?customerId=${customerId}&customerName=${encodeURIComponent(customerName)}`)
                }
              >
                すべて表示（{licenseCount}件）
              </Button>
            </Box>
            {licensesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : licenses && licenses.length > 0 ? (
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>ライセンス名</TableCell>
                      <TableCell align="right">Lic数</TableCell>
                      <TableCell align="right">割当数</TableCell>
                      <TableCell>前回更新日</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {licenses.slice(0, 15).map((l) => {
                      const overAssigned =
                        l.assignedCount != null &&
                        l.licenseCount != null &&
                        l.assignedCount > l.licenseCount;
                      return (
                        <TableRow
                          key={l.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() =>
                            navigate(`/licenses?customerId=${customerId}&customerName=${encodeURIComponent(customerName)}`)
                          }
                        >
                          <TableCell>{l.licenseName}</TableCell>
                          <TableCell align="right">{l.licenseCount ?? '—'}</TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: overAssigned ? '#d32f2f' : undefined, fontWeight: overAssigned ? 'bold' : undefined }}
                          >
                            {l.assignedCount ?? '—'}
                          </TableCell>
                          <TableCell>{formatDate(l.lastRenewalDate)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                ライセンス情報はありません
              </Typography>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default CustomerKartePage;
